import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Check, X, Leaf, ShieldCheck, Calendar, Clock } from "lucide-react";
import { fmtEur2, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  bozza: { color: "bg-zen-subtle/40 text-zen-ink", label: "Bozza" },
  inviato: { color: "bg-zen-info/20 text-zen-info", label: "In attesa di firma" },
  accettato: { color: "bg-zen-primary/20 text-zen-primary", label: "Accettato" },
  rifiutato: { color: "bg-zen-alert/15 text-zen-alert", label: "Rifiutato" },
  scaduto: { color: "bg-zen-alert/15 text-zen-alert", label: "Scaduto" },
};

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [signOpen, setSignOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [signAgree, setSignAgree] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedInfo, setAcceptedInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/quotes/${token}`);
      setData(data);
      setSignName(data.client?.name || "");
    } catch (e) {
      setError(e.response?.data?.detail || "Preventivo non trovato");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const accept = async () => {
    if (!signName.trim() || signName.trim().length < 3) return toast.error("Inserisci il tuo nome completo");
    if (!signAgree) return toast.error("Conferma di aver letto e accettato i termini");
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(
        `${API}/public/quotes/${token}/accept`,
        { full_name: signName.trim() },
      );
      setAcceptedInfo(res);
      setSignOpen(false);
      toast.success("Preventivo firmato! Riceverai la fattura via email.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore firma");
    } finally { setSubmitting(false); }
  };

  const reject = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/reject`, { reason: rejectReason });
      setRejectOpen(false);
      toast.success("Preventivo rifiutato. Grazie per la risposta.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zen-bg flex items-center justify-center text-zen-ink2">
        Caricamento preventivo…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-zen-bg flex items-center justify-center p-6">
        <div className="zen-card p-8 max-w-md text-center">
          <X size={40} strokeWidth={1.2} className="mx-auto text-zen-alert mb-3"/>
          <h1 className="font-heading text-2xl">Preventivo non disponibile</h1>
          <p className="text-zen-ink2 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { quote, client, issuer } = data;
  const t = quote.totals;
  const isAccepted = quote.status === "accettato";
  const isRejected = quote.status === "rifiutato";
  const isExpired = quote.status === "scaduto";
  const canAct = !isAccepted && !isRejected && !isExpired;
  const badge = STATUS_STYLES[quote.status] || STATUS_STYLES.inviato;

  return (
    <div className="min-h-screen bg-zen-bg py-8 px-4" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {issuer.company_logo_url ? (
              <img
                src={issuer.company_logo_url.startsWith("http") ? issuer.company_logo_url : `${process.env.REACT_APP_BACKEND_URL}${issuer.company_logo_url}`}
                alt="Logo"
                className="h-12 max-w-[120px] object-contain"
              />
            ) : (
              <img
                src="/brand/freelanzen-wordmark.png"
                alt="FreelanZen"
                className="h-8 w-auto object-contain"
              />
            )}
            <div>
              <div className="font-heading text-lg text-zen-ink">{issuer.company_name}</div>
              {issuer.company_vat && <div className="text-xs text-zen-ink2">P.IVA {issuer.company_vat}</div>}
            </div>
          </div>
          <Badge className={`${badge.color} rounded-full font-medium text-sm`} data-testid="quote-status">
            {badge.label}
          </Badge>
        </div>

        {/* Main card */}
        <div className="zen-card p-6 md:p-10 space-y-8">
          <div>
            <span className="zen-label">Preventivo N° {quote.number}</span>
            <h1 className="font-heading text-3xl mt-2">{quote.title || "Preventivo"}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-zen-ink2 mt-3">
              <div className="flex items-center gap-1.5"><Calendar size={13}/> Emesso: {fmtDate(quote.issued_date)}</div>
              <div className="flex items-center gap-1.5"><Clock size={13}/> Valido fino al: {fmtDate(quote.valid_until)}</div>
            </div>
          </div>

          {client?.name && (
            <div>
              <div className="zen-label">Destinatario</div>
              <div className="mt-1 font-medium">{client.name}{client.company ? ` · ${client.company}` : ""}</div>
              {client.email && <div className="text-sm text-zen-ink2">{client.email}</div>}
            </div>
          )}

          {/* Items */}
          <div>
            <div className="zen-label mb-2">Voci</div>
            <div className="divide-y divide-zen-border border border-zen-border rounded-xl overflow-hidden">
              {(quote.items || []).map((it, i) => (
                <div key={i} className="p-4 flex gap-4 items-start hover:bg-zen-bg/40 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="text-zen-ink">{it.description}</div>
                    <div className="text-xs text-zen-ink2 mt-0.5">
                      {it.quantity} × {fmtEur2(it.unit_price)}
                    </div>
                  </div>
                  <div className="font-medium text-zen-ink whitespace-nowrap">
                    {fmtEur2(it.quantity * it.unit_price)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 gap-y-1.5 text-sm max-w-xs ml-auto">
            <span className="text-zen-ink2">Subtotale</span>
            <span className="text-right">{fmtEur2(t.subtotal)}</span>
            {t.discount > 0 && (
              <>
                <span className="text-zen-ink2">Sconto {quote.discount_pct}%</span>
                <span className="text-right">− {fmtEur2(t.discount)}</span>
                <span className="text-zen-ink2">Imponibile</span>
                <span className="text-right">{fmtEur2(t.taxable)}</span>
              </>
            )}
            <span className="text-zen-ink2">IVA {quote.iva_pct}%</span>
            <span className="text-right">{fmtEur2(t.iva)}</span>
            <span className="font-heading text-xl text-zen-primary pt-2 border-t border-zen-border mt-1">Totale</span>
            <span className="font-heading text-xl text-zen-primary text-right pt-2 border-t border-zen-border mt-1" data-testid="public-total">
              {fmtEur2(t.total)}
            </span>
          </div>

          {quote.notes && (
            <div>
              <div className="zen-label mb-1">Note</div>
              <div className="text-sm text-zen-ink2 whitespace-pre-wrap">{quote.notes}</div>
            </div>
          )}
          {quote.terms && (
            <div>
              <div className="zen-label mb-1">Termini e condizioni</div>
              <div className="text-sm text-zen-ink2 whitespace-pre-wrap">{quote.terms}</div>
            </div>
          )}

          {/* Signature status */}
          {isAccepted && (
            <div
              data-testid="accepted-banner"
              className="p-5 rounded-xl bg-zen-primary/10 border border-zen-primary/30 flex items-start gap-3"
            >
              <Check size={22} className="text-zen-primary shrink-0 mt-0.5"/>
              <div className="flex-1 text-sm">
                <div className="font-medium text-zen-primary">Preventivo accettato</div>
                <div className="text-zen-ink2 mt-1">
                  Firmato da <b>{quote.accepted_by_name}</b>
                  {quote.accepted_at && ` il ${new Date(quote.accepted_at).toLocaleString("it-IT")}`}.
                  {acceptedInfo?.invoice_number && (
                    <div className="mt-2">
                      È stata generata la fattura <b>{acceptedInfo.invoice_number}</b> da {fmtEur2(acceptedInfo.invoice_amount)}.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {isRejected && (
            <div className="p-5 rounded-xl bg-zen-alert/10 border border-zen-alert/30 flex items-start gap-3">
              <X size={22} className="text-zen-alert shrink-0 mt-0.5"/>
              <div className="flex-1 text-sm">
                <div className="font-medium text-zen-alert">Preventivo rifiutato</div>
                {quote.rejection_reason && (
                  <div className="text-zen-ink2 mt-1">Motivo: {quote.rejection_reason}</div>
                )}
              </div>
            </div>
          )}
          {isExpired && (
            <div className="p-5 rounded-xl bg-zen-alert/10 border border-zen-alert/30 text-sm text-zen-alert">
              Questo preventivo è scaduto. Contatta {issuer.company_email || issuer.company_name} per una nuova offerta.
            </div>
          )}

          {/* Actions */}
          {canAct && !signOpen && !rejectOpen && (
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-zen-border">
              <Button
                data-testid="accept-open-btn"
                onClick={() => setSignOpen(true)}
                className="bg-zen-primary hover:bg-zen-primaryHover rounded-full flex-1 h-12 text-base"
              >
                <Check size={18} className="mr-2"/> Accetta e firma online
              </Button>
              <Button
                data-testid="reject-open-btn"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                className="rounded-full h-12 border-zen-border"
              >
                <X size={16} className="mr-2"/> Rifiuta
              </Button>
            </div>
          )}

          {/* Sign inline form */}
          {signOpen && (
            <div className="p-6 border border-zen-primary/30 bg-zen-primary/5 rounded-xl space-y-4" data-testid="sign-form">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-zen-primary"/>
                <h3 className="font-heading text-lg">Firma digitale</h3>
              </div>
              <p className="text-sm text-zen-ink2">
                Confermando la firma, accetti formalmente il preventivo. Verrà emessa
                la fattura <b>{fmtEur2(t.total)}</b> e riceverai copia via email.
              </p>
              <div>
                <Label>Nome e cognome completo *</Label>
                <Input
                  data-testid="sign-name"
                  value={signName}
                  onChange={(e) => setSignName(e.target.value)}
                  placeholder="Mario Rossi"
                  autoFocus
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-zen-ink2 cursor-pointer">
                <input
                  data-testid="sign-agree"
                  type="checkbox"
                  checked={signAgree}
                  onChange={(e) => setSignAgree(e.target.checked)}
                  className="mt-1 accent-zen-primary"
                />
                <span>
                  Confermo di aver letto il preventivo e i termini, e di accettarlo formalmente
                  come dichiarazione di volontà ai sensi dell&apos;art. 20 CAD.
                </span>
              </label>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Button
                  data-testid="sign-confirm"
                  onClick={accept}
                  disabled={submitting}
                  className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
                >
                  {submitting ? "Firmo…" : "Firma e accetta"}
                </Button>
                <Button variant="ghost" onClick={() => setSignOpen(false)}>Annulla</Button>
              </div>
            </div>
          )}
          {rejectOpen && (
            <div className="p-6 border border-zen-alert/30 bg-zen-alert/5 rounded-xl space-y-4" data-testid="reject-form">
              <h3 className="font-heading text-lg text-zen-alert">Rifiuta preventivo</h3>
              <div>
                <Label>Motivo (opzionale)</Label>
                <Textarea
                  data-testid="reject-reason"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Es. budget non compatibile, tempi non allineati…"
                />
              </div>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Button
                  data-testid="reject-confirm"
                  onClick={reject} disabled={submitting}
                  className="bg-zen-alert hover:bg-zen-alert/90 text-white rounded-full"
                >
                  {submitting ? "Invio…" : "Conferma rifiuto"}
                </Button>
                <Button variant="ghost" onClick={() => setRejectOpen(false)}>Annulla</Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-zen-ink2">
          Preventivo firmato in sicurezza tramite <b>FreelanZen</b>. La firma è tracciata con timestamp e IP.
        </div>
      </div>
    </div>
  );
}
